import {
  Alert,
  Box,
  Card,
  Chip,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import { Edit, Schedule, AttachMoney, CheckCircle, Cancel } from '@mui/icons-material';
import React, { useContext, useState, useEffect } from 'react';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { InvoiceFrequencyPeriodTypeLabels } from '../../../../enums/extra-charges';
import { displayMoney } from '../../../../utils/money';

interface InvoiceFrequencySectionProps {
  merchant: MerchantType;
  onUpdate?: (updatedMerchant: MerchantType) => void;
  canEdit: boolean;
  onOpenDialog?: () => void;
}

function InvoiceFrequencySection({
  merchant,
  canEdit,
  onOpenDialog,
}: InvoiceFrequencySectionProps) {
  const { settings: orgSettings } = useContext(OrgSettingsContext);

  // Helper function to get effective settings
  const getEffectiveSettings = () => {
    const hasCustom = merchant.invoice_frequency_enabled !== null && merchant.invoice_frequency_enabled !== undefined;
    
    if (hasCustom) {
      return {
        enabled: merchant.invoice_frequency_enabled ?? false,
        periodType: merchant.invoice_frequency_period_type,
        limit: merchant.invoice_frequency_limit,
        chargeAmount: merchant.invoice_frequency_charge_amount,
        isCustom: true,
      };
    }
    return {
      enabled: orgSettings.invoice_frequency_enabled ?? false,
      periodType: orgSettings.invoice_frequency_period_type,
      limit: orgSettings.invoice_frequency_limit,
      chargeAmount: orgSettings.invoice_frequency_charge_amount,
      isCustom: false,
    };
  };

  // State for effective settings (will be recalculated when merchant changes)
  const [effectiveSettings, setEffectiveSettings] = useState(() => getEffectiveSettings());

  // Update effective settings when merchant or org settings change
  useEffect(() => {
    const newSettings = getEffectiveSettings();
    console.log('New effective settings:', newSettings);
    setEffectiveSettings(newSettings);
  }, [merchant, orgSettings]);



  return (
    <>
      <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Invoice Frequency Charges
            </Typography>
          </Box>
          {canEdit && onOpenDialog && (
            <IconButton 
              onClick={onOpenDialog}
              size="small"
              sx={{ 
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Status with visual indicator */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              {effectiveSettings.enabled ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
              ) : (
                <Cancel sx={{ color: 'text.disabled', fontSize: 20 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {effectiveSettings.enabled ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
            <Chip 
              label={effectiveSettings.isCustom ? 'Custom' : 'Default'} 
              size="small"
              variant={effectiveSettings.isCustom ? 'filled' : 'outlined'}
              color={effectiveSettings.isCustom ? 'primary' : 'default'}
            />
          </Box>

          {effectiveSettings.enabled ? (
            <>
              <Divider />
              
              {/* Visual summary of settings */}
              <Box sx={{ 
                backgroundColor: 'grey.50', 
                borderRadius: 1, 
                p: 1.5,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="caption" color="textSecondary" sx={{ 
                  textTransform: 'uppercase', 
                  fontWeight: 600,
                  letterSpacing: 0.5
                }}>
                  Current Rules
                </Typography>
                
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Period */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Period:</strong> {effectiveSettings.periodType 
                        ? InvoiceFrequencyPeriodTypeLabels[effectiveSettings.periodType as keyof typeof InvoiceFrequencyPeriodTypeLabels]
                        : 'Not configured'
                      }
                    </Typography>
                  </Box>
                  
                  {/* Limit */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 600 }}>
                      📊
                    </Typography>
                    <Typography variant="body2">
                      <strong>Limit:</strong> {effectiveSettings.limit !== null && effectiveSettings.limit !== undefined 
                        ? `${effectiveSettings.limit} invoices`
                        : 'Not configured'
                      }
                    </Typography>
                  </Box>
                  
                  {/* Charge */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoney sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="body2">
                      <strong>Extra Charge:</strong> {effectiveSettings.chargeAmount && effectiveSettings.chargeAmount.length > 0 
                        ? displayMoney(effectiveSettings.chargeAmount)
                        : 'Not configured'
                      }
                    </Typography>
                  </Box>
                </Box>
              </Box> 

              {/* Quick explanation */}
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Extra charges apply when merchant exceeds {effectiveSettings.limit || 0} invoices per {effectiveSettings.periodType?.toLowerCase().replace('_', ' ') || 'period'}
                </Typography>
              </Alert>
            </>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 2,
              color: 'text.secondary'
            }}>
              <Typography variant="body2">
                Invoice frequency charges are disabled
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {effectiveSettings.isCustom ? 'Custom setting' : 'Following organization default'}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
}

export default InvoiceFrequencySection;